import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import UpgradeModal from "@/components/UpgradeModal";
import AuthPage from "@/pages/AuthPage";
import LandingPage from "@/pages/LandingPage";
import DemoVideoPage from "@/pages/DemoVideoPage";
import AppShell from "@/components/AppShell";
import LearnersPage from "@/pages/LearnersPage";
import LearnerDetailPage from "@/pages/LearnerDetailPage";
import CardsPage from "@/pages/CardsPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import TeamPage from "@/pages/TeamPage";
import CompliancePage from "@/pages/CompliancePage";
import AuditPage from "@/pages/AuditPage";
import ConsentPage from "@/pages/ConsentPage";
import BillingPage from "@/pages/BillingPage";
import ConceptGroupsPage from "@/pages/ConceptGroupsPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import JoinPage from "@/pages/JoinPage";
// TermsPage + PrivacyPage exist under /app/frontend/src/pages/ but are intentionally
// NOT routed yet — awaiting legal review before publishing to the marketing site.
import BetaOnboardingGate from "@/components/BetaOnboardingGate";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentReturnPage";

function IdleGuard({ children }) {
  useIdleLogout();
  return children;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return <IdleGuard>{children}</IdleGuard>;
}

function AuthOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/learners" replace />;
  return children;
}

function PublicRoot() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/learners" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <div className="App grain">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <UpgradeModal />
          <Routes>
            <Route path="/" element={<PublicRoot />} />
            <Route path="/auth" element={<AuthOnly><AuthPage /></AuthOnly>} />
            <Route path="/consent/:token" element={<ConsentPage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/demo-video" element={<DemoVideoPage />} />
            <Route element={<Protected><BetaOnboardingGate><AppShell /></BetaOnboardingGate></Protected>}>
              <Route path="learners" element={<LearnersPage />} />
              <Route path="learners/:id" element={<LearnerDetailPage />} />
              <Route path="cards" element={<CardsPage />} />
              <Route path="activities" element={<ActivitiesPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="compliance" element={<CompliancePage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="concepts" element={<ConceptGroupsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin" element={<AdminPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
